import { useI18n } from 'next-localization';
import { useEffect, useMemo, useState } from 'react';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import useHelpers from 'hooks/helpers';
import Checkbox from 'components/Generic/Checkbox';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import AbrdnSelect from 'components/Generic/Forms/FormFields/AbrdnSelect';
import { benchmarkTranslationKeys, colorCodes, monthTranslationKeys } from './config';
import { standardFormatShort } from '@/constants/dateFormats';
import usePayload from 'hooks/useFundDetailsRequestPayload';
import {
  useFetchCumulativeChartDataQuery,
  useLazyFetchCumulativeChartDataQuery,
} from '@/store/fundDetails/services/fundDetailsApi';
import FundDetailsPerformanceTabCumulativeChart from 'components/Pages/FundsCenter/FundDetails/FundDetailsPerformanceTabCumulativeSection/FundDetailsPerformanceTabCumulativeChart';
import styles from './FundDetailsPerformanceTabCumulativeSection.module.scss';
import BaseSkeleton from 'components/Generic/BaseSkeleton';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

const getTranslationKeyByMonths = (months: number) => {
  let translationKey;

  if (months === 1) {
    translationKey = 'monthNumber';
  } else if (months < 12) {
    translationKey = 'monthsNumber';
  } else if (months === 12) {
    translationKey = 'yearNumber';
  } else {
    translationKey = 'yearsNumber';
  }

  return translationKey;
};

const getAbrdnSelectFields = (
  options: Array<{ label: number | string; value: number }>,
  selectedValue: number | string
) => {
  if (!options) return [];
  return options.map(({ value, label }) => ({
    label,
    value: value,
    selected: value === selectedValue,
  }));
};

const FundDetailsPerformanceTabCumulativeSection = () => {
  const { t } = useI18n();
  const { getErrorDictionary, localizeDate } = useHelpers();
  const { sitecoreContext } = useSitecoreContext();
  const { performanceChartSettings } = sitecoreContext.fundsConfiguration;
  const defaultOption: number | string = performanceChartSettings?.defaultOption;

  const payload = usePayload();
  const { data: chartData, isFetching, error } = useFetchCumulativeChartDataQuery(payload);
  const [
    getFilteredData,
    { data: filteredData, isFetching: isFilteredDataFetching, error: filteredDataError },
  ] = useLazyFetchCumulativeChartDataQuery();
  const [selectedDateFilterPreset, setSelectedDateFilterPreset] = useState<number | string>('');
  const [availableDateFilterPresets, setAvailableDateFilterPresets] = useState([]);
  const [selectedMinYear, setSelectedMinYear] = useState<number>(null);
  const [selectedMaxYear, setSelectedMaxYear] = useState<number>(null);
  const [selectedMaxMonth, setSelectedMaxMonth] = useState<number>(null);
  const [selectedMinMonth, setSelectedMinMonth] = useState<number>(null);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const dateFilterPresets: Array<{ months: number; translationKey: string }> =
    performanceChartSettings?.availableOptions?.map((months) => ({
      months: Number(months),
      translationKey: getTranslationKeyByMonths(Number(months)),
    }));

  const benchmarks = useMemo(() => {
    if (!chartData) return [];
    return Object.entries(chartData?.benchmarks).filter(([, value]) => value);
  }, [chartData]);

  const dateFilterPresetOptions = useMemo(() => {
    return getAbrdnSelectFields(
      availableDateFilterPresets.map((preset) => ({
        value: preset.months,
        label: t(preset.translationKey).replace(
          '{token.value}',
          preset.months >= 12 ? preset.months / 12 : preset.months
        ),
      })),
      selectedDateFilterPreset
    );
  }, [availableDateFilterPresets, selectedDateFilterPreset, t]);

  const renderDateFilterPresetSelector = useMemo(() => {
    return dateFilterPresetOptions?.length > 1;
  }, [dateFilterPresetOptions]);

  const getMonthsByYear = (year: number): Array<number> => {
    if (!chartData) return [];
    return chartData?.categories[0].values
      .filter(({ date }) => new Date(date).getUTCFullYear() === year)
      .map(({ date }) => new Date(date).getUTCMonth());
  };

  const getMinMaxValues = (preset) => {
    const maxMonths = Array.from(new Set(getMonthsByYear(years[years.length - 1])));
    const minDate = new Date(years[years.length - 1], maxMonths[maxMonths.length - 1] - preset + 1);
    const minYear = minDate.getUTCFullYear();
    const minMonth = minDate.getUTCMonth();
    const selectedMinYear: number =
      preset === 'custom' ? years[0] : years.includes(minYear) ? minYear : years[0];
    const minMonths = Array.from(new Set(getMonthsByYear(selectedMinYear)));
    const selectedMinMonth =
      preset === 'custom' ? minMonths[0] : minMonths.includes(minMonth) ? minMonth : minMonths[0];
    return {
      maxMonths,
      selectedMinYear,
      minMonths,
      selectedMinMonth,
    };
  };

  const years = useMemo((): Array<number> => {
    return Array.from(
      new Set(chartData?.categories[0].values.map(({ date }) => new Date(date).getUTCFullYear()))
    );
  }, [chartData]);

  const maxMonths = useMemo(() => {
    const uniqueValues = new Set(getMonthsByYear(selectedMaxYear));
    return Array.from(uniqueValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaxYear]);

  const minMonths = useMemo(() => {
    const uniqueValues = new Set(getMonthsByYear(selectedMinYear));
    return Array.from(uniqueValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMinYear]);

  const showingResultsFromTo = useMemo(() => {
    if (
      !chartData ||
      !selectedMinYear ||
      !selectedMinMonth ||
      !selectedMaxYear ||
      !selectedMaxMonth
    ) {
      return;
    }

    const { values } = chartData.categories[0];

    const mappedValues = values.map((value) => {
      const date = new Date(value.date);
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth();
      return { year, month, date: value.date };
    });

    const minDate = mappedValues.find(({ year, month }) => {
      return year === selectedMinYear && month === selectedMinMonth;
    })?.date;

    const maxDate = mappedValues.find(({ year, month }) => {
      return year === selectedMaxYear && month === selectedMaxMonth;
    })?.date;

    if (minDate && maxDate) {
      return t('showingResultsFromTo')
        .replace('{token.dateFrom}', localizeDate(new Date(minDate), standardFormatShort))
        .replace('{token.dateTo}', localizeDate(new Date(maxDate), standardFormatShort));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, selectedMinYear, selectedMinMonth, selectedMaxYear, selectedMaxMonth]);

  const getDefaultSelectedDateFilterPreset = (
    defaultOption: number | string,
    updatedState: Array<{ months: number; translationKey: string }>
  ): number | string => {
    let defaultSelectedDateFilterPreset;

    if (!isNaN(defaultOption)) {
      defaultSelectedDateFilterPreset = Number(defaultOption);
    }

    const preset = updatedState.find((preset) => preset.months === defaultSelectedDateFilterPreset);

    return preset?.months || 'custom';
  };

  const minYearOptions = useMemo(() => {
    return getAbrdnSelectFields(
      years.map((year) => ({ value: year, label: year })),
      selectedMinYear
    );
  }, [years, selectedMinYear]);

  const minMonthOptions = useMemo(() => {
    return getAbrdnSelectFields(
      minMonths.map((month) => ({ value: month, label: t(monthTranslationKeys[month]) })),
      selectedMinMonth
    );
  }, [minMonths, selectedMinMonth, t]);

  const maxYearOptions = useMemo(() => {
    return getAbrdnSelectFields(
      years.map((year) => ({ value: year, label: year })),
      selectedMaxYear
    );
  }, [years, selectedMaxYear]);

  const maxMonthOptions = useMemo(() => {
    return getAbrdnSelectFields(
      maxMonths.map((month) => ({ value: month, label: t(monthTranslationKeys[month]) })),
      selectedMaxMonth
    );
  }, [maxMonths, selectedMaxMonth, t]);

  // set up Select default value. Depends on initial response
  useEffect(() => {
    if (!chartData) return;
    const customPreset = { months: 'custom', translationKey: 'custom' };
    const { values } = chartData?.categories[0];
    const firstDate = new Date(values[0].date);
    firstDate.setDate(1);

    const updatedPresets = [
      ...dateFilterPresets.filter((preset) => {
        const presetDate = new Date(values[values.length - 1].date);
        presetDate.setMonth(presetDate.getMonth() - preset.months + 1);
        presetDate.setDate(1);
        return firstDate <= presetDate;
      }),
      customPreset,
    ];

    const defaultPreset: number | string = getDefaultSelectedDateFilterPreset(
      defaultOption,
      updatedPresets
    );
    const selectedCategories = chartData?.categories.reduce(
      (selectedCategories, category) => ({
        ...selectedCategories,
        [category.label]: true,
      }),
      {}
    );

    setAvailableDateFilterPresets(updatedPresets);
    setSelectedDateFilterPreset(defaultPreset);
    setSelectedCategories(selectedCategories);

    const { maxMonths, selectedMinYear, selectedMinMonth } = getMinMaxValues(defaultPreset);

    setSelectedMinYear(selectedMinYear);
    setSelectedMinMonth(selectedMinMonth);
    setSelectedMaxYear(years[years.length - 1]);
    setSelectedMaxMonth(maxMonths[maxMonths.length - 1]);

    if (selectedDateFilterPreset !== 'custom') {
      fetchFilteredData(
        selectedMinYear,
        selectedMinMonth,
        years[years.length - 1],
        maxMonths[maxMonths.length - 1]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  useEffect(() => {
    if (selectedDateFilterPreset === 'custom') {
      setSelectedMinMonth(minMonths[0]);
      setSelectedMaxMonth(maxMonths[maxMonths.length - 1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minMonths, maxMonths]);

  const fetchFilteredData = (
    selectedMinYear,
    selectedMinMonth,
    selectedMaxYear,
    selectedMaxMonth
  ) => {
    const fromDate = new Date(Date.UTC(selectedMinYear, selectedMinMonth, 1))
      .toISOString()
      .split('T')[0];

    const toDate = new Date(Date.UTC(selectedMaxYear, selectedMaxMonth + 1, 0))
      .toISOString()
      .split('T')[0];
    getFilteredData({ ...payload, fromDate, toDate });
  };

  function handleCLick(value: number | string) {
    setSelectedDateFilterPreset(isNaN(value) ? value : Number(value));
    const { maxMonths, selectedMinYear, minMonths, selectedMinMonth } = getMinMaxValues(value);

    if (value === 'custom') {
      setSelectedMinYear(years[0]);
      setSelectedMinMonth(minMonths[0]);
    } else {
      setSelectedMinYear(selectedMinYear);
      setSelectedMinMonth(selectedMinMonth);
      setSelectedMaxYear(years[years.length - 1]);
      setSelectedMaxMonth(maxMonths[maxMonths.length - 1]);
    }

    if (!value) return;
    if (value !== 'custom') {
      fetchFilteredData(
        selectedMinYear,
        selectedMinMonth,
        years[years.length - 1],
        maxMonths[maxMonths.length - 1]
      );
    }
  }

  return (
    <div className="d-flex flex-column gap-4">
      <h4>{t('fundDetailsTabsPerformanceCumulative')}</h4>
      {error || filteredDataError ? (
        <AlertMessageBase type="error" title={t(getErrorDictionary(error))} />
      ) : isFetching || isFilteredDataFetching ? (
        <BaseSkeleton height="100%" />
      ) : chartData ? (
        <>
          {renderDateFilterPresetSelector && (
            <div className="row">
              <div className="col-12 col-lg-4">
                <div className="d-flex flex-column gap-1">
                  <div className="form-label text-nowrap">{t('showResultsFrom')}</div>
                  <AbrdnSelect
                    defaultValue={dateFilterPresetOptions.find((option) => option.selected)}
                    options={dateFilterPresetOptions}
                    className={styles.dropdown}
                    onChange={({ value }) => handleCLick(value)}
                  />
                </div>
              </div>

              {showingResultsFromTo && selectedDateFilterPreset !== 'custom' && (
                <div className="mt-2">{showingResultsFromTo}</div>
              )}
            </div>
          )}

          {selectedDateFilterPreset === 'custom' && (
            <div>
              <div className="mb-2 body-default">{t('fundDetailsPerformanceChartHelp')}</div>

              <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-4">
                <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-1 gap-lg-2">
                  <div className="form-label text-nowrap">{t('dateFrom')}</div>

                  <div className="d-flex align-items-center gap-1">
                    <AbrdnSelect
                      options={minYearOptions}
                      defaultValue={minYearOptions.find((option) => option.selected)}
                      className={`${styles.dropdown}`}
                      onChange={({ value }) => setSelectedMinYear(+value)}
                    />
                    <AbrdnSelect
                      options={minMonthOptions}
                      defaultValue={minMonthOptions.find((option) => option.selected)}
                      className={`${styles.dropdown} ${styles['dropdown--month']} mt-0`}
                      onChange={({ value }) => setSelectedMinMonth(+value)}
                    />
                  </div>
                </div>

                <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-1 gap-lg-2">
                  <div className="form-label text-nowrap">{t('dateTo')}</div>

                  <div className="d-flex align-items-center gap-1">
                    <AbrdnSelect
                      options={maxYearOptions}
                      defaultValue={maxYearOptions.find((option) => option.selected)}
                      className={`${styles.dropdown}`}
                      onChange={({ value }) => setSelectedMaxYear(+value)}
                    />
                    <AbrdnSelect
                      options={maxMonthOptions}
                      defaultValue={maxMonthOptions.find((option) => option.selected)}
                      className={`${styles.dropdown} ${styles['dropdown--month']} mt-0`}
                      onChange={({ value }) => setSelectedMaxMonth(+value)}
                    />
                  </div>
                </div>

                <AbrdnButton
                  buttonType="primary"
                  disabled={isFilteredDataFetching}
                  className="align-self-start justify-content-center"
                  onClick={() =>
                    fetchFilteredData(
                      selectedMinYear,
                      selectedMinMonth,
                      selectedMaxYear,
                      selectedMaxMonth
                    )
                  }
                >
                  <span>{t('filterResults')}</span>
                </AbrdnButton>
              </div>
            </div>
          )}

          <FundDetailsPerformanceTabCumulativeChart
            selectedCategories={selectedCategories}
            chartData={filteredData ? filteredData.categories : chartData?.categories}
            isLoading={isFetching || isFilteredDataFetching}
          />

          {chartData && chartData.categories.length > 1 && (
            <div>
              <div className="body-default">{t('fundDetailsPerformanceChartKey')}:</div>

              <div className="mt-2 d-flex flex-column flex-lg-row gap-2 gap-lg-4 flex-wrap">
                {chartData.categories.map((category, index) => (
                  <div key={category.label}>
                    <Checkbox
                      selected={selectedCategories[category.label]}
                      label={category.label}
                      customColor={colorCodes[index]}
                      onChange={() =>
                        setSelectedCategories((prevState) => ({
                          ...prevState,
                          [category.label]: !prevState[category.label],
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {benchmarks.length !== 0 && (
            <div>
              {benchmarks.map(([key, value], index) => (
                <div key={key} className={`footnote text-grey ${index > 0 ? 'mt-1' : ''}`}>
                  {t(benchmarkTranslationKeys[key])}: {value}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="body-default">{t('noResultsMessage')}</div>
      )}
    </div>
  );
};

export default FundDetailsPerformanceTabCumulativeSection;
